import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_7886c98d from 'nuxt_plugin_plugin_7886c98d' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_sentryserver_a913e632 from 'nuxt_plugin_sentryserver_a913e632' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_45bc9b5f from 'nuxt_plugin_sentryclient_45bc9b5f' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_nuxtmq_f2b2b72a from 'nuxt_plugin_nuxtmq_f2b2b72a' // Source: ./nuxt-mq.js (mode: 'all')
import nuxt_plugin_pluginutils_47598f10 from 'nuxt_plugin_pluginutils_47598f10' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_6b30f07a from 'nuxt_plugin_pluginrouting_6b30f07a' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_1e75328c from 'nuxt_plugin_pluginmain_1e75328c' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_vlazyload_0e5756d1 from 'nuxt_plugin_vlazyload_0e5756d1' // Source: ./v-lazy-load.js (mode: 'all')
import nuxt_plugin_googleanalytics_215857ba from 'nuxt_plugin_googleanalytics_215857ba' // Source: ./google-analytics.js (mode: 'client')
import nuxt_plugin_toast_ea0d265c from 'nuxt_plugin_toast_ea0d265c' // Source: ./toast.js (mode: 'client')
import nuxt_plugin_workbox_9a5bcf02 from 'nuxt_plugin_workbox_9a5bcf02' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_7a867092 from 'nuxt_plugin_metaplugin_7a867092' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_axios_826dbfba from 'nuxt_plugin_axios_826dbfba' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_cookieuniversalnuxt_7687cc6a from 'nuxt_plugin_cookieuniversalnuxt_7687cc6a' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_swiperjs_1bf9dd60 from 'nuxt_plugin_swiperjs_1bf9dd60' // Source: ../plugins/swiperjs.js (mode: 'client')
import nuxt_plugin_goftino_330d40f9 from 'nuxt_plugin_goftino_330d40f9' // Source: ../plugins/goftino.js (mode: 'client')
import nuxt_plugin_bottomsheet_328e8059 from 'nuxt_plugin_bottomsheet_328e8059' // Source: ../plugins/bottomsheet.js (mode: 'client')
import nuxt_plugin_chart_48b5b2cf from 'nuxt_plugin_chart_48b5b2cf' // Source: ../plugins/chart.js (mode: 'client')
import nuxt_plugin_veevalidate_1a0c1998 from 'nuxt_plugin_veevalidate_1a0c1998' // Source: ../plugins/vee-validate.js (mode: 'client')
import nuxt_plugin_filters_2dd71012 from 'nuxt_plugin_filters_2dd71012' // Source: ../plugins/filters.js (mode: 'all')
import nuxt_plugin_helper_850e665a from 'nuxt_plugin_helper_850e665a' // Source: ../plugins/helper.js (mode: 'all')
import nuxt_plugin_variables_4c3fcf96 from 'nuxt_plugin_variables_4c3fcf96' // Source: ../plugins/variables.js (mode: 'all')
import nuxt_plugin_number_5baed150 from 'nuxt_plugin_number_5baed150' // Source: ../plugins/number.js (mode: 'all')
import nuxt_plugin_upload_8ca86cc0 from 'nuxt_plugin_upload_8ca86cc0' // Source: ../plugins/upload.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"arzypto","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=0"},{"itemprop":"description","content":"صرافی آنلاین ارزیپتو ، خرید و فروش ارز دیجیتال ،نقل و انتقال سریع و ایمن، پشتیبانی از بیش از ۴۰۰+ رمز ارز، تحلیل و بررسی و اخبار فوری ارز دیجیتال، پاسخگوی تمام نیازهای معاملاتی شما"},{"property":"og:description","content":"صرافی آنلاین ارزیپتو ، خرید و فروش ارز دیجیتال ،نقل و انتقال سریع و ایمن، پشتیبانی از بیش از ۴۰۰+ رمز ارز، تحلیل و بررسی و اخبار فوری ارز دیجیتال، پاسخگوی تمام نیازهای معاملاتی شما"},{"hid":"description","name":"description","content":"صرافی آنلاین ارزیپتو ، خرید و فروش ارز دیجیتال ،نقل و انتقال سریع و ایمن، پشتیبانی از بیش از ۴۰۰+ رمز ارز، تحلیل و بررسی و اخبار فوری ارز دیجیتال، پاسخگوی تمام نیازهای معاملاتی شما"},{"property":"og:title","content":"ارزیپتو | خرید و فروش ارز دیجیتال | صرافی آنلاین"},{"property":"og:image","content":"https:\u002F\u002Fcdn.arzypto.com\u002Fimages\u002Ffavicon\u002Ffavicon-16x16.png"},{"property":"og:url","content":"https:\u002F\u002Farzypto.com"}],"link":[{"rel":"apple-touch-icon","sizes":"180x180","href":"https:\u002F\u002Fcdn.arzypto.com\u002Fimages\u002Ffavicon\u002Fapple-touch-icon.png"},{"rel":"icon","type":"image\u002Fpng","sizes":"32x32","href":"https:\u002F\u002Fcdn.arzypto.com\u002Fimages\u002Ffavicon\u002Ffavicon-32x32.png"},{"rel":"icon","type":"image\u002Fpng","sizes":"16x16","href":"https:\u002F\u002Fcdn.arzypto.com\u002Fimages\u002Ffavicon\u002Ffavicon-16x16.png"},{"rel":"mask-icon","href":"images\u002Ffavicon\u002Fsafari-pinned-tab.svg","color":"#5bbad5"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_7886c98d === 'function') {
    await nuxt_plugin_plugin_7886c98d(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_sentryserver_a913e632 === 'function') {
    await nuxt_plugin_sentryserver_a913e632(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_45bc9b5f === 'function') {
    await nuxt_plugin_sentryclient_45bc9b5f(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtmq_f2b2b72a === 'function') {
    await nuxt_plugin_nuxtmq_f2b2b72a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_47598f10 === 'function') {
    await nuxt_plugin_pluginutils_47598f10(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_6b30f07a === 'function') {
    await nuxt_plugin_pluginrouting_6b30f07a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_1e75328c === 'function') {
    await nuxt_plugin_pluginmain_1e75328c(app.context, inject)
  }

  if (typeof nuxt_plugin_vlazyload_0e5756d1 === 'function') {
    await nuxt_plugin_vlazyload_0e5756d1(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googleanalytics_215857ba === 'function') {
    await nuxt_plugin_googleanalytics_215857ba(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_toast_ea0d265c === 'function') {
    await nuxt_plugin_toast_ea0d265c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_9a5bcf02 === 'function') {
    await nuxt_plugin_workbox_9a5bcf02(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_7a867092 === 'function') {
    await nuxt_plugin_metaplugin_7a867092(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_826dbfba === 'function') {
    await nuxt_plugin_axios_826dbfba(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_7687cc6a === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_7687cc6a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_swiperjs_1bf9dd60 === 'function') {
    await nuxt_plugin_swiperjs_1bf9dd60(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_goftino_330d40f9 === 'function') {
    await nuxt_plugin_goftino_330d40f9(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_bottomsheet_328e8059 === 'function') {
    await nuxt_plugin_bottomsheet_328e8059(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_chart_48b5b2cf === 'function') {
    await nuxt_plugin_chart_48b5b2cf(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_veevalidate_1a0c1998 === 'function') {
    await nuxt_plugin_veevalidate_1a0c1998(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_2dd71012 === 'function') {
    await nuxt_plugin_filters_2dd71012(app.context, inject)
  }

  if (typeof nuxt_plugin_helper_850e665a === 'function') {
    await nuxt_plugin_helper_850e665a(app.context, inject)
  }

  if (typeof nuxt_plugin_variables_4c3fcf96 === 'function') {
    await nuxt_plugin_variables_4c3fcf96(app.context, inject)
  }

  if (typeof nuxt_plugin_number_5baed150 === 'function') {
    await nuxt_plugin_number_5baed150(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_upload_8ca86cc0 === 'function') {
    await nuxt_plugin_upload_8ca86cc0(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
